import { ChangeDetectorRef, Component, inject, ViewContainerRef } from '@angular/core';

import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { AbilityFacade } from '@core/abilities-legacy';
import { Ability } from '@core/abilities-types';
import { CurrentUserService } from '@core/account-logic';
import type { User } from '@core/account-types';
import type { BillingPlan } from '@core/billing-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import { OfferKind } from '@core/offers-types';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  AdminsAvatarsComponent,
  BadgeComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogRef,
  HeadingComponent,
  PlanButtonComponent,
} from '@core/user-interface-legacy';
import { BillingPlansDataService } from '@recruitee/billing-logic';
import { ChooseYourPlanContext, SampleDataAnalyticsService } from '@recruitee/company-logic';

import { getActiveProductOrThrow } from '../../helpers/get-active-product-or-throw';
import { readyOnce } from '../../helpers/rx-operators';
import type { PricingFeatureLoadedModel } from '../../types/pricing-feature.types';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../types/pricing-feature.types';

enum ViewType {
  GetInTouch = 'GetInTouch',
  Upgrade = 'Upgrade',
  UpgradePlan = 'UpgradePlan',
  AdminsAvatars = 'AdminsAvatars',
}

@Component({
  selector: 'rt-offer-slots-limit-dialog',
  templateUrl: './offer-slots-limit-dialog.component.html',
  styleUrls: ['./offer-slots-limit-dialog.component.less'],
  imports: [
    BadgeComponent,
    HeadingComponent,
    DialogBodyComponent,
    ButtonComponent,
    PlanButtonComponent,
    AdminsAvatarsComponent,
    TranslatePipe,
  ],
})
export class OfferSlotsLimitDialogComponent
  implements DialogComponentI<{ offerKind: OfferKind; isTrial?: boolean }, boolean | null>
{
  private readonly billingPlansData = inject(BillingPlansDataService);
  private readonly currentUserData = inject(CurrentUserService);
  private readonly abilityFacade = inject(AbilityFacade);
  private readonly ADMINS_LIMIT = 4;
  public readonly TRIAL_SLOTS_LIMIT: number = 5;
  public model: PricingFeatureLoadedModel;
  public viewType: ViewType;
  public ViewType: typeof ViewType = ViewType;
  public OfferKind: typeof OfferKind = OfferKind;
  public teamMembers: User[] = [];
  public slotsTotal: number;
  public offerKind: OfferKind;
  public showSaveAsDraft: boolean;
  public isTrial: boolean;
  public heading: string;
  public description: string;

  private ngOnDestroy$ = new Subject<void>();
  private activePlan: BillingPlan | undefined;
  private trialDaysLeft: number;
  private triggeredFromBulkCount: number;
  private jobSlotsUsed: number;
  private skipRedirectAfterPurchase: boolean;
  private billingPlans: BillingPlan[];

  constructor(
    private model$: PricingFeatureObservableModel,
    private cdRef: ChangeDetectorRef,
    private vcRef: ViewContainerRef,
    private intercomService: IntercomService,
    private viewRef: ViewContainerRef,
    private facade: PricingFeatureFacade,
    private sampleDataAnalyticsService: SampleDataAnalyticsService,
    private dialogRef: DialogRef,
    private store: Store,
    private i18n: I18nService,
  ) {
    this.facade.fetchPlansIfNeeded();
  }

  public ngOnInit(): void {
    combineLatest([
      this.model$.pipe(readyOnce),
      this.billingPlansData.activePlan$,
      this.billingPlansData.collection$,
      this.currentUserData.currentCompany$,
    ])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([model, activePlan, billingPlans, currentCompany]) => {
        this.model = model;
        this.trialDaysLeft = moment(currentCompany!.trialPeriodEnd).diff(moment(), 'days', false);
        this.jobSlotsUsed = currentCompany!.usedJobs;
        this.activePlan = activePlan;
        this.billingPlans = billingPlans;
        this.cdRef.detectChanges();
      });

    this.abilityFacade
      .checkAbility(Ability.ManageSubscription)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabled => (enabled ? this.setViewType() : this.setAdminsAvatarsViewType()));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public setDialogData(
    data: {
      offerKind: OfferKind;
      showSaveAsDraft?: boolean;
      isTrial: boolean;
      triggeredFromBulkCount?: number;
      skipRedirectAfterPurchase?: boolean;
    },
    triggerUpdate: boolean,
  ): void {
    this.offerKind = data.offerKind;
    this.showSaveAsDraft = data.showSaveAsDraft || false;
    this.isTrial = data.isTrial;
    this.triggeredFromBulkCount = data.triggeredFromBulkCount || 0;
    this.skipRedirectAfterPurchase = data.skipRedirectAfterPurchase || false;
    this.setHeading();
  }

  public openIntercom(): void {
    this.intercomService.open();
  }

  public openPlanPickerDialog(): void {
    const product = getActiveProductOrThrow(this.model, this.billingPlans, this.activePlan);

    this.dialogRef.close();

    if (this.isTrial || this.model.isPP24) {
      this.facade.openPlanPicker({
        skipRedirectOnSuccess: this.skipRedirectAfterPurchase,
      });
      this.sampleDataAnalyticsService.trackChooseYourPlan(
        ChooseYourPlanContext.JobSlotsLimitModal,
        this.model.hasSampleData,
        this.trialDaysLeft,
      );
    } else {
      this.facade.openSlotsPickerDialog(
        product!,
        this.model,
        this.model.subscription?.manual,
        this.vcRef,
      );
    }
  }

  public saveAsDraft() {
    this.dialogRef.close(true);
  }

  private setViewType(): void {
    this.model$
      .pipe(
        readyOnce,
        takeUntil(this.ngOnDestroy$),
        map(model => ({ isManual: model.subscription?.manual, isPP24: model.isPP24 })),
      )
      .subscribe(({ isManual, isPP24 }) => {
        let type: ViewType;
        if (isManual) {
          type = ViewType.GetInTouch;
        } else if (this.isTrial) {
          type = ViewType.Upgrade;
        } else if (isPP24) {
          type = ViewType.UpgradePlan;
        } else {
          type = ViewType.Upgrade;
        }
        this.viewType = type;
        this.setHeading();
        this.setDescription();
      });
  }

  private setAdminsAvatarsViewType(): void {
    this.facade
      .getAdminsWithManageSubscriptionAbility()
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(teamMembers => {
        this.teamMembers = teamMembers;
        this.viewType = ViewType.AdminsAvatars;
        this.setHeading();
        this.setDescription();
      });
  }

  private setHeading(): void {
    if (this.triggeredFromBulkCount) {
      this.heading =
        this.i18n.translate('app.pricing.slots_limit.title_for_bulk_1') +
        '<br>' +
        this.i18n.translate('app.pricing.slots_limit.title_for_bulk_2');
    } else if (this.viewType === ViewType.UpgradePlan) {
      this.heading = this.i18n.translate('app.pricing.slots_limit.title_unlimited_slots');
    } else {
      this.heading = this.i18n.translate('app.pricing.slots_limit.title');
    }
    this.cdRef.detectChanges();
  }

  private setDescription(): void {
    this.description = this.getJobsDescription();
    this.cdRef.detectChanges();
  }

  private getJobsDescription(): string {
    const slotsTotal = this.isTrial ? this.TRIAL_SLOTS_LIMIT : this.model.slotsTotal;
    const totalRetrieveCount = this.triggeredFromBulkCount;
    const canRetrieveCount = slotsTotal - this.jobSlotsUsed;
    let params;
    if (totalRetrieveCount > 0) {
      params = { slotsTotal, totalRetrieveCount, canRetrieveCount };
      if (this.isTrial) {
        return this.i18n.translate('app.pricing.slots_limit.description.jobs_bulk_trial', params);
      }
      if (this.viewType === ViewType.GetInTouch) {
        return this.i18n.translate('app.pricing.slots_limit.description.jobs_bulk_manual', params);
      }
      if (this.viewType === ViewType.AdminsAvatars) {
        return this.i18n.translate(
          'app.pricing.slots_limit.description.jobs_bulk_ask_to_upgrade',
          params,
        );
      }
      return this.i18n.translate('app.pricing.slots_limit.description.jobs_bulk_upgrade', params);
    } else {
      params = { slotsTotal };
      if (this.isTrial) {
        return this.i18n.translate('app.pricing.slots_limit.description.jobs_trial', params);
      } else if (this.model.isPP24) {
        return this.i18n.translate('app.pricing.slots_limit.description.jobs_unlimited_slots');
      } else {
        return this.i18n.translate('app.pricing.slots_limit.description.jobs', params);
      }
    }
  }
}
